import { useEffect, useState } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts'
import { useTradingStore } from '../hooks/useTradingStore'
import { Activity, Clock, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react'
import clsx from 'clsx'
import LogOutput from '../components/LogOutput'

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

export default function Monitoring() {
  const {
    metrics, alerts, clearAlerts, strategyStatus, fetchMetrics, fetchStrategies,
    accountBalance, accountCurrency, marginInitial, marginMaintenance,
    strategyStartedAt, equityCurve, fetchEquityCurve,
  } = useTradingStore()

  const [uptime, setUptime] = useState(0)

  // Sync strategy status from server on mount so uptime starts correctly
  // even when the user navigates directly to this page.
  useEffect(() => {
    const controller = new AbortController()
    fetchStrategies()
    fetchMetrics(controller.signal)
    fetchEquityCurve(controller.signal)
    const interval = setInterval(() => fetchMetrics(controller.signal), 5000)
    // Server samples equity ~1/min; poll the curve at the same cadence
    const equityInterval = setInterval(() => fetchEquityCurve(controller.signal), 60000)
    return () => {
      controller.abort()
      clearInterval(interval)
      clearInterval(equityInterval)
    }
  }, [fetchStrategies, fetchMetrics, fetchEquityCurve])

  // Drive uptime from the server-stamped start time so it survives navigation.
  useEffect(() => {
    if (strategyStatus === 'running' && strategyStartedAt) {
      const startMs = new Date(strategyStartedAt).getTime()
      const tick = () => setUptime(Math.floor((Date.now() - startMs) / 1000))
      tick()
      const interval = setInterval(tick, 1000)
      return () => clearInterval(interval)
    }
    setUptime(0)
  }, [strategyStatus, strategyStartedAt])

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Algorithm Monitoring</h1>
        <div className={clsx(
          'status-indicator',
          strategyStatus === 'running' ? 'running' : strategyStatus === 'warning' ? 'warning' : 'stopped'
        )}>
          <Activity size={14} className={strategyStatus === 'running' ? 'pulse' : ''} />
          <span>
            {strategyStatus === 'running' ? 'Running' : strategyStatus === 'warning' ? 'Warning' : 'Stopped'}
          </span>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatusCard
          title="Uptime"
          value={strategyStatus === 'running' ? formatUptime(uptime) : 'Stopped'}
          icon={Clock}
          color={strategyStatus === 'running' ? 'blue' : 'gray'}
        />
        <StatusCard
          title="Total Trades"
          value={metrics.total_trades.toString()}
          icon={Activity}
          color="purple"
        />
        <StatusCard
          title="Win/Loss"
          value={`${metrics.profitable_trades}/${metrics.losing_trades}`}
          icon={metrics.profitable_trades > metrics.losing_trades ? TrendingUp : TrendingDown}
          color={metrics.profitable_trades > metrics.losing_trades ? 'green' : 'red'}
        />
        <StatusCard
          title="Alerts"
          value={alerts.length.toString()}
          icon={AlertTriangle}
          color={alerts.length > 0 ? 'yellow' : 'gray'}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2 gap-6">
        {/* Equity Curve */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Equity Curve</h2>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={equityCurve}>
                <defs>
                  <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4488ff" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#4488ff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis dataKey="time" stroke="#666" fontSize={10} />
                <YAxis stroke="#666" fontSize={10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #2a2a2a',
                    borderRadius: '8px',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="equity"
                  stroke="#4488ff"
                  fillOpacity={1}
                  fill="url(#colorEquity)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* PnL Chart */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Cumulative P&L</h2>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={equityCurve}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis dataKey="time" stroke="#666" fontSize={10} />
                <YAxis stroke="#666" fontSize={10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #2a2a2a',
                    borderRadius: '8px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="pnl"
                  stroke="#00ff88"
                  dot={false}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Account & Margin */}
      {accountBalance != null && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Account</h2>
            {accountCurrency && <span className="text-xs text-[#666]">{accountCurrency}</span>}
          </div>
          <div className="grid grid-cols-3 gap-4 p-4">
            <div>
              <p className="text-xs text-[#666] uppercase mb-1">Balance</p>
              <p className="text-xl font-bold text-white">
                {accountBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-xs text-[#666] uppercase mb-1">Initial Margin</p>
              <p className={`text-xl font-bold ${(marginInitial ?? 0) > 0 ? 'text-yellow-400' : 'text-white'}`}>
                {(marginInitial ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-xs text-[#666] uppercase mb-1">Maintenance Margin</p>
              <p className={`text-xl font-bold ${(marginMaintenance ?? 0) > 0 ? 'text-orange-400' : 'text-white'}`}>
                {(marginMaintenance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Logs */}
      <LogOutput />

      {/* Alerts Log */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">System Alerts</h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#666]">Last 50</span>
            {alerts.length > 0 && (
              <button
                onClick={clearAlerts}
                className="text-xs text-[#666] hover:text-white transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          {alerts.length === 0 ? (
            <div className="text-center py-8 text-[#666]">No alerts</div>
          ) : (
            <div className="space-y-2">
              {alerts.slice().reverse().map((alert, index) => (
                <div
                  key={index}
                  className={clsx(
                    'flex items-start gap-3 p-3 rounded',
                    alert.level === 'error' && 'bg-red-500/10',
                    alert.level === 'warning' && 'bg-yellow-500/10',
                    alert.level === 'info' && 'bg-blue-500/10'
                  )}
                >
                  <AlertTriangle
                    size={16}
                    className={clsx(
                      'mt-0.5',
                      alert.level === 'error' && 'text-red-400',
                      alert.level === 'warning' && 'text-yellow-400',
                      alert.level === 'info' && 'text-blue-400'
                    )}
                  />
                  <div className="flex-1">
                    <p className="text-sm text-white">{alert.message}</p>
                    <p className="text-xs text-[#666] mt-1">
                      {new Date(alert.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface StatusCardProps {
  title: string
  value: string
  icon: React.ComponentType<{ size?: number | string; className?: string }>
  color: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'gray'
}

function StatusCard({ title, value, icon: Icon, color }: StatusCardProps) {
  const colorClasses = {
    blue: 'bg-blue-500/10 text-blue-400',
    green: 'bg-green-500/10 text-green-400',
    red: 'bg-red-500/10 text-red-400',
    yellow: 'bg-yellow-500/10 text-yellow-400',
    purple: 'bg-purple-500/10 text-purple-400',
    gray: 'bg-gray-500/10 text-gray-400',
  }

  return (
    <div className="card flex items-center gap-4">
      <div className={clsx('w-12 h-12 rounded-lg flex items-center justify-center', colorClasses[color])}>
        <Icon size={24} />
      </div>
      <div>
        <p className="text-xs text-[#666] uppercase">{title}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
      </div>
    </div>
  )
}
